/**
 * The Access screen (ADR-034): one app's roles in one org workspace —
 * Workspace Controls → an approved app → Access.
 *
 * URL: /workspaces/:holderId/apps/:archeId/access/:section — the links the
 * server's notices and emails carry. Every member reaches "My access";
 * access managers and reviewers also see People, Requests, Reviews and
 * Settings. The server decides every one of those answers (a non-manager
 * gets the same 404 as an unknown app); the navigation only avoids offering
 * a section that would refuse.
 *
 * "Admin" means two things here, and the page says so: a workspace admin
 * manages the app's roles, but what anyone can do with the app's data —
 * admins included — follows the roles they hold.
 *
 * An app whose approval was revoked while its roles remain is still shown
 * (`admitted: false`): its roles reach nothing, and the page says so above
 * every section — removing roles, reviews and the export stay open; giving
 * roles, accepting, turning roles on and changing settings wait for it to be
 * approved again (the server refuses them; the sections do not offer them).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SettingsSideNav } from '@/components/SettingsSideNav';
import { useWorkspaceMembers } from '../hooks/useWorkspaceTeam';
import { useAppAccessScreen, useMyAppAccess } from '../hooks/useAppAccess';
import {
  APP_ACCESS_SECTIONS,
  appAccessSectionPath,
  describeAppAccessError,
  isAppAccessSectionId,
  type AppAccessSectionId,
} from './app-access/shared';
import { PeopleSection } from './app-access/PeopleSection';
import { RequestsSection } from './app-access/RequestsSection';
import { ReviewsSection } from './app-access/ReviewsSection';
import { SettingsSection } from './app-access/SettingsSection';
import { MyAccessSection } from './app-access/MyAccessSection';
import { MembersLoadError } from './app-access/members';

export function AppAccessPage() {
  const params = useParams<{ holderId: string; archeId: string; section?: string }>();
  const navigate = useNavigate();
  const holderId = params.holderId ?? '';
  const archeId = params.archeId ?? '';
  const scope = { holderId, archeId };
  const mine = useMyAppAccess(scope);
  const governs = !!mine.data && (mine.data.seat.manager || mine.data.seat.reviewer);
  const screen = useAppAccessScreen(scope, governs);
  const members = useWorkspaceMembers(governs ? holderId : null);
  const [grantFor, setGrantFor] = useState<{ principalId: string; roleId: string | null } | null>(
    null,
  );

  const sections = APP_ACCESS_SECTIONS.filter((s) => governs || !s.governance);
  const fallback: AppAccessSectionId = governs ? 'people' : 'my-access';
  const requested = isAppAccessSectionId(params.section) ? params.section : null;
  const section: AppAccessSectionId =
    requested && sections.some((s) => s.id === requested) ? requested : fallback;

  // Keep the URL naming the section shown (a bare link, or a section this
  // person is not offered, lands on their default).
  useEffect(() => {
    if (!mine.data) return;
    if (params.section !== section) {
      navigate(appAccessSectionPath(holderId, archeId, section), { replace: true });
    }
  }, [mine.data, params.section, section, holderId, archeId, navigate]);

  const appName = mine.data?.appName ?? 'This app';
  const notAdmitted = mine.data?.admitted === false || screen.data?.admitted === false;

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8">
          <p className="text-sm">
            <Link to="/workspaces" className="text-muted-foreground hover:underline">
              Workspace Controls
            </Link>
          </p>
          <h1 className="text-2xl font-bold text-foreground">Access — {appName}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who can use {appName}’s shared workspace data here. Once its roles are on, what anyone
            can do with that data — owners and admins included — follows the roles they hold; owners
            and admins manage the roles. Each person’s own data in the app, and the workspace’s
            files, keep their usual rules.
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {notAdmitted && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {governs
              ? 'This app is no longer approved in this workspace; you can still remove roles and export, but not give new ones.'
              : 'This app is no longer approved in this workspace. The roles you hold reach nothing until it is approved again; you can still give them up.'}
          </div>
        )}
        {mine.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : mine.isError || !mine.data ? (
          <p className="text-sm text-destructive">
            {describeAppAccessError(mine.error, 'This app is not available in this workspace.')}
          </p>
        ) : (
          <div className="md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:items-start md:gap-8">
            <SettingsSideNav
              label="Access sections"
              className="mb-4 md:sticky md:top-4 md:mb-0"
              items={sections.map((s) => ({
                to: appAccessSectionPath(holderId, archeId, s.id),
                label: s.label,
                icon: s.icon,
              }))}
            />
            <div className="min-w-0 space-y-6">
              {section === 'my-access' ? (
                <MyAccessSection scope={scope} appName={appName} mine={mine.data} />
              ) : screen.isLoading || members.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : screen.isError || !screen.data ? (
                <p className="text-sm text-destructive">{describeAppAccessError(screen.error)}</p>
              ) : members.isError ? (
                // Never an empty workspace: every section names its people.
                <MembersLoadError onRetry={() => void members.refetch()} />
              ) : section === 'people' ? (
                <PeopleSection
                  scope={scope}
                  appName={appName}
                  screen={screen.data}
                  members={members.data ?? []}
                  grantFor={grantFor}
                />
              ) : section === 'requests' ? (
                <RequestsSection
                  scope={scope}
                  screen={screen.data}
                  members={members.data ?? []}
                  onGrant={(r) => {
                    setGrantFor(r);
                    navigate(appAccessSectionPath(holderId, archeId, 'people'));
                  }}
                />
              ) : section === 'reviews' ? (
                <ReviewsSection scope={scope} screen={screen.data} members={members.data ?? []} />
              ) : (
                <SettingsSection
                  scope={scope}
                  appName={appName}
                  screen={screen.data}
                  members={members.data ?? []}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
